import { useDispatch } from 'react-redux';
import { store } from '../store/store';
import Logger from '../common/logger';
import { bookingWriteApiSlice } from '../api/booking/bookingWriteApiSlice';
import { setIsConfirmationDialog, setIsLoading } from '../store/slices/globalSlice';
import { useParams } from 'react-router';

// Hard delete lives on the new (ECS) backend: DELETE /admin/order/:id
// (bookingWriteApiSlice, master-only server-side). No RTK cache tags are
// touched — see the note at the call site.
const deleteBooking = async (bookingId: string) => {
    // unwrap() makes a backend failure reject instead of resolving with
    // `{ error }` — without it a failed delete (e.g. the server-side 403
    // master gate) showed the success snackbar while the booking survived.
    const data = await store.dispatch(bookingWriteApiSlice.endpoints.deleteBooking.initiate(bookingId)).unwrap();
    return data;
};

export const useDeleteBooking = () => {
    const dispatch = useDispatch();
    const params = useParams<{ bookingId: string }>();
    const orderId = params.bookingId ?? '';

    const fetchApartments = async () => {
        try {
            dispatch(setIsLoading(true));
            await deleteBooking(orderId);
            // Deliberately NO cache invalidation here.
            //
            // This flow runs while the user is still on the deleted booking's
            // own route, and `getBookingDetails` provides BOTH 'bookingDetails'
            // and 'bookingList' — so either tag refetches `GET /order/:id` for
            // the order we just deleted. It 404s, and `BookingDetailsPage`
            // swaps to "Buchung nicht gefunden" until the calling page's
            // delayed navigate fires. The delete had already succeeded; the
            // error was purely our own doing (observed on production
            // 2026-08-10).
            //
            // Nothing is lost by omitting it: every caller navigates to a
            // booking list, and `BookingsPage` fetches on mount with
            // `forceRefetch: true` (fetchBookingsFlow), so the list is fresh
            // either way.
            dispatch(setIsConfirmationDialog(false));
            return true;
        } catch (error) {
            Logger('deleteBooking').error(error);
            dispatch(setIsConfirmationDialog(false));
            // Rethrow so the calling page shows its error snackbar instead
            // of treating a failed delete as success.
            throw error;
        } finally {
            dispatch(setIsLoading(false));
        }
    };

    return fetchApartments;
};
